import { authConfig } from '@/libs/auth-config';
import { tokenManager, type AuthTokens } from '@/libs/token-manager';

interface RefreshTokenResponse {
  success: boolean;
  data?: {
    tokens: AuthTokens;
  };
  message?: string;
}

export type RefreshFailureReason =
  | 'no_refresh_token'
  | 'auth_failure'
  | 'server_error'
  | 'timeout'
  | 'network_error'
  | 'invalid_response';

export interface RefreshResult {
  success: boolean;
  reason?: RefreshFailureReason;
}

/**
 * Refresh the access token using the refresh token
 * Returns an object with success status and failure reason if applicable
 */
export async function refreshAccessToken(): Promise<RefreshResult> {
  try {
    const refreshToken = tokenManager.getRefreshToken();

    if (!refreshToken) {
      console.log('[REFRESH-TOKEN] No refresh token available');
      return { success: false, reason: 'no_refresh_token' };
    }

    const apiUrl = authConfig.getApiUrl();

    console.log('[REFRESH-TOKEN] Attempting to refresh access token');

    const response = await fetch(`${apiUrl}/auth/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
      // Add timeout to prevent hanging
      signal: AbortSignal.timeout(10000), // 10 second timeout
    });

    if (!response.ok) {
      // Only clear tokens for auth failures (401, 403), not network failures (5xx)
      if (response.status === 401 || response.status === 403) {
        console.warn('[REFRESH-TOKEN] Auth failure, clearing tokens');
        tokenManager.clearTokens();
        return { success: false, reason: 'auth_failure' };
      }

      // Server errors (5xx) - keep tokens for retry
      console.warn('[REFRESH-TOKEN] Server error, keeping tokens for retry:', response.status);
      return { success: false, reason: 'server_error' };
    }

    // Parse the response to get new tokens
    const data = (await response.json()) as RefreshTokenResponse;

    if (data.success && data.data?.tokens) {
      const { tokens } = data.data;

      // Update the access token in the token manager
      tokenManager.updateAccessToken(tokens.accessToken, tokens.expiresIn);

      console.log('[REFRESH-TOKEN] Access token refreshed successfully');
      return { success: true };
    }

    console.warn('[REFRESH-TOKEN] Invalid refresh response format');
    return { success: false, reason: 'invalid_response' };
  } catch (error) {
    // Network errors (timeout, no connection, DNS failure, etc.)
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        console.warn('[REFRESH-TOKEN] Request timed out');
        return { success: false, reason: 'timeout' };
      }
      if (error.name === 'TypeError') {
        console.warn('[REFRESH-TOKEN] Network error:', error.message);
        return { success: false, reason: 'network_error' };
      }
      console.warn('[REFRESH-TOKEN] Unexpected error:', error.message);
    }
    // Don't clear tokens on network errors - allow retry on next attempt
    return { success: false, reason: 'network_error' };
  }
}
